import React, { useEffect, useState } from 'react';
import {
  Button,
  FormField,
  Icon,
  Input,
  Link,
  Typography,
} from 'components/alf-design';
import { useForm, validate as externalValidator, useApi } from 'utils';
import { datadogLogs } from '@datadog/browser-logs';
import { Guidance } from 'components/layout/login';
import { westPointCustomerPortalLinkProps } from 'components/layout/login/WestPointPolicyErrorMessage';
import { LegacyColors } from 'constants';

function validate(values) {
  const errors = {};

  if (!values?.lastName && values?.policyNumber) {
    errors.lastName = 'Required';
  }

  if (!values?.policyNumber) {
    errors.policyNumber = 'Policy number is invalid.';
  } else {
    const msgError = externalValidator.policyNumberPayment(
      values.policyNumber.trim().toUpperCase().replace(/ /g, ''),
    );
    if (msgError) {
      errors.policyNumber = msgError;
    }
  }

  return errors;
}

const Initial = ({ policyNumber, setStep, setPaymentData }) => {
  const api = useApi();
  const [error, setError] = useState(null);

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    initForm,
    values,
    errors,
    touched,
  } = useForm(
    {
      policyNumber,
      lastName: '',
    },
    validate,
    true,
  );
  const isWestPointPolicy =
    touched.policyNumber &&
    externalValidator.isWestPointPolicy(values.policyNumber);

  const getPaymentInfo = async (policyNumber, lastName) => {
    try {
      const res = await api.getPolicyPayments(policyNumber, lastName);
      if (res.data) {
        return res.data;
      }
      setError(
        'Policy not found or is not available for payment. Please verify the information and try again, or contact Slide Customer Service.',
      );
      return null;
    } catch (error) {
      setError(
        'Policy not found or is not available for payment. Please verify the information and try again, or contact Slide Customer Service.',
      );

      datadogLogs.logger.error(`Load Payment information - ${policyNumber}`, {
        error,
      });
      return null;
    }
  };

  const onSubmit = (e, values) => {
    handleSubmit(e, async () => {
      e.preventDefault();
      if (Object.keys(errors).length > 0) {
        return;
      }
      const data = await getPaymentInfo(
        values?.policyNumber.replace(/ /g, '').trim().toUpperCase(),
        values?.lastName,
      );
      if (data) {
        if (data?.billing === null) {
          setError(
            "We're currently experiencing technical difficulties. Please try again later.",
          );
          return;
        }
        setPaymentData(data);
        setStep('summary');
      }
    });
  };

  useEffect(() => {
    if (policyNumber !== values.policyNumber) {
      initForm({ policyNumber });
    }
  }, [policyNumber]);

  return (
    <div className="grid gap-[20px]">
      <div>
        <Typography type="subtitle" className="!text-[15px] xl:!text-[18px]">
          Let's get your current billing information.
        </Typography>
      </div>
      {error && (
        <div className="flex bg-legacy-errorBackground p-[15px]">
          <Icon
            name="timesCircleFull"
            color={LegacyColors.errorIcon}
            fontSize={25}
          />
          <Typography type="error" className="text-[16px] w-[80%] ml-[15px]">
            {error}
          </Typography>
        </div>
      )}
      <div className="block md:flex md:justify-between">
        <div className="w-auto md:w-[48%]">
          <FormField
            label="Policy Number"
            error={
              touched.policyNumber && !isWestPointPolicy
                ? errors?.policyNumber
                : ''
            }
            icon={<Guidance />}
          >
            <Input
              placeholder="Type policy number"
              name="policyNumber"
              id="policyNumber"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values?.policyNumber}
              error={!!errors?.policyNumber && !!touched?.policyNumber}
              success={!errors?.policyNumber && !!touched?.policyNumber}
            />
          </FormField>
          {isWestPointPolicy && (
            <Typography type="error" component="p">
              Oops! This policy lives in a different customer portal.
              <br />
              Your policy is managed in a separate system. You'll be able to
              view your policy and make payments there.
              <br />
              Use the link below to sign in or register.
              <br />
              <Link
                {...westPointCustomerPortalLinkProps}
                className="!text-legacy-error underline font-bold"
              >
                Go to your customer portal
              </Link>
            </Typography>
          )}
        </div>
        <div className="w-auto md:w-[48%]">
          <FormField
            label="Policyholder Last Name or Company/DBA Name"
            error={touched.lastName ? errors?.lastName : ''}
            divClassName="flex"
          >
            <Input
              placeholder="Type policyholder last name or company/DBA name"
              name="lastName"
              id="lastName"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.lastName}
              error={!!errors?.lastName && !!touched?.lastName}
              success={!errors?.lastName && !!touched?.lastName}
            />
          </FormField>
        </div>
      </div>
      <div>
        <Typography type="subtitle" className="!text-[15px] xl:!text-[18px]">
          We accept online payments from your:
        </Typography>
        <ul className="pl-[20px]">
          <li>
            <Typography>Credit Card</Typography>
          </li>
          <li>
            <Typography>Personal Checking Account</Typography>
          </li>
          <li>
            <Typography>Personal Savings Account</Typography>
          </li>
        </ul>
      </div>
      <div className="text-justify">
        If there are questions regarding this payment or if technical
        difficulties occur, please contact Slide Insurance Company Customer
        Service{' '}
        <Link
          data-testid="customer-service"
          href={`tel:800-748-2030`}
          className="!text-legacy-secondary font-black"
          tabIndex={0}
          type="primary"
        >
          (800) 748-2030.
        </Link>
      </div>
      <div>
        <Typography type="subtitle" className="!font-normal">
          Slide Insurance Company is pleased to offer you the convenience and
          ease of making secured online payments. Please be advised that making
          an online payment does not immediately change any disposition related
          to your account. Any payment made after normal business hours may not
          be posted until the next business day. We reserve the right to decline
          the acceptance of an online payment if your account is in default for
          any reason. Payments will be accepted from a credit card or personal
          checking or savings account.
        </Typography>
      </div>
      <div>
        <Typography type="subtitle">
          It is your responsibility to enter the correct routing/account
          information or card information. Any transactions declined due to data
          entry error will incur an insufficient funds processing fee of $25.
        </Typography>
      </div>
      <div className="text-center mt-[10px]">
        <Button
          id="continue-login"
          type="brand"
          className="min-w-[120px] md:min-w-[320px]"
          onClick={(e) => onSubmit(e, values)}
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

export default Initial;
